
import React, { useState } from 'react';
import Pagination from './Pagination';
import ReviewCard from './ReviewCard';
import { SafeReviews } from '../types';

interface ReviewsSectionProps {
    reviews: SafeReviews[];
    totalReviews: number;
    averageRating?: number;
    listingId?: string | null;
    diveCenterName: string | null;
    diveCenterRating: number | null;
    tripAdvisorLocationId?: string | null;
    averageRatingTripAdvisor?: number | null;
    totalReviewsTripAdvisor?: number | null;
}

interface ReviewListProps {
    reviews: SafeReviews[] | undefined;
}

const ReviewList: React.FC<ReviewListProps> = ({ reviews }) => (
    <div className="mt-4 space-y-8">
        {reviews?.map((review) => (
            <ReviewCard
                key={review.id}
                review={review}
            />
        ))}
    </div>
);

const ReviewsSection: React.FC<ReviewsSectionProps> = ({
    reviews,
    totalReviews,
    listingId,
    diveCenterName,
    diveCenterRating,
    tripAdvisorLocationId,
    averageRatingTripAdvisor,
    totalReviewsTripAdvisor,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [showListingReviews, setShowListingReviews] = useState(true);
    const reviewsPerPage = 5;

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const toggleListingReviews = () => {
        setShowListingReviews((prev) => !prev);
        setCurrentPage(1);
    };

    const belongsToListing = (review: SafeReviews) => {
        if (!review.reservation) return false;
        if (!review.reservation.listing) return false;
        return review.reservation.listing.id === listingId;
    };

    const filteredReviews = React.useMemo(() => {
        if (!listingId) return reviews;
        return showListingReviews ? reviews.filter(belongsToListing) : reviews;
    }, [reviews, listingId, showListingReviews]);

    const totalPages = Math.ceil((filteredReviews?.length || 0) / reviewsPerPage);

    return (
        <div>
            <div className="flex justify-between mt-12 items-center">
                <div id="reviews" className="font-bold flex flex-col md:flex-row items-start md:items-center">
                    <div className="sm:text-2xl text-xl">
                        {totalReviews === 0 && totalReviewsTripAdvisor && tripAdvisorLocationId
                            ? `Reviews (${totalReviewsTripAdvisor})`
                            : showListingReviews && listingId
                                ? `Listing's reviews (${filteredReviews.length})`
                                : `Reviews (${totalReviews})`}
                    </div>
                    {totalReviews > 0 && diveCenterName && diveCenterRating && (
                        <span className="hidden md:block ml-4 text-neutral-600 font-semibold text-md">
                            {diveCenterName} has a rating of {diveCenterRating.toFixed(1)}
                        </span>
                    )}
                </div>
                {listingId && totalReviews > 0 && (
                    <button
                        onClick={toggleListingReviews}
                        className="bg-cyan-600 hover:bg-cyan-500 px-4 py-2 text-white rounded-lg"
                    >
                        {showListingReviews ? 'Show all reviews' : 'Show listing reviews'}
                    </button>
                )}
            </div>

            {totalReviews === 0 ? (
                tripAdvisorLocationId && (averageRatingTripAdvisor || totalReviewsTripAdvisor) ? (
                    <div className="mt-6">
                        <div className="flex items-center gap-3">
                            {averageRatingTripAdvisor && (
                                <span className="text-lg font-semibold">{averageRatingTripAdvisor.toFixed(1)}</span>
                            )}
                            {totalReviewsTripAdvisor && (
                                <span className="text-neutral-600">{totalReviewsTripAdvisor} reviews</span>
                            )}
                        </div>
                        <a
                            href={/^g\d+/.test(tripAdvisorLocationId)
                                ? `https://www.tripadvisor.com/Attraction_Review-${tripAdvisorLocationId}.html`
                                : `https://www.tripadvisor.com/Attraction_Review-d${tripAdvisorLocationId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-2 text-cyan-700 font-medium hover:underline"
                        >
                            <img
                                src="https://www.tripadvisor.com/img/cdsi/img2/branding/v2/Tripadvisor_lockup_horizontal_secondary_registered-11900-2.svg"
                                alt="TripAdvisor"
                                className="h-5"
                            />
                            View reviews on TripAdvisor
                        </a>
                    </div>
                ) : (
                    <div className="mt-6 text-md text-gray-500">
                        This dive center has no reviews yet
                    </div>
                )
            ) : filteredReviews?.length === 0 ? (
                <div className="mt-6 text-md text-gray-500">
                    No reviews found for this listing
                </div>
            ) : (
                <>
                    <ReviewList
                        reviews={filteredReviews}
                    />
                    {totalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default ReviewsSection;